import React, { useState, useEffect, useRef } from "react";
import { number, string, shape, arrayOf, object, bool } from "prop-types";
import cx from "classnames";
// eslint-disable-next-line import/no-cycle
import {
	SecondaryCard,
	SenaryCard,
	PrimaryCard,
	TertiaryCard,
	QuaternaryCard,
	CruiseCard,
	BrandCard,
	BlogCard,
	SingleCard,
	NoBorderCard,
	SquareCard,
} from "../../Card";
import { cloudinaryImageToSrcUrlAdapter } from "../../../sharedComponents/CloudinaryImageAdapter";
import { Cell, Row } from "../../Grid/Grid";
import { Icon } from "../../Icons";
import * as styles from "./PaginatedCollection.module.scss";

const PaginatedCollection = ({
	variant,
	paginatedEntries,
	entriesPerPage,
	largeTiles,
	pagesPerGroup = 5,
}) => {
	const [allowPrevious, setAllowPrevious] = useState(false);
	const [allowNext, setAllowNext] = useState(false);
	const [paginationItems, setPaginationItems] = useState([]);
	const [currentPage, setCurrentPage] = useState(1);
	const prevCurrentPage = useRef();

	const totalPaginatedEntries = Math.ceil(paginatedEntries?.length / entriesPerPage);
	const chunkedPaginatedEntries = [];

	let index = 0;
	while (index < paginatedEntries.length) {
		chunkedPaginatedEntries.push(paginatedEntries.slice(index, entriesPerPage + index));
		index += entriesPerPage;
	}

	const handlePrevNextPageLogic = page => {
		if (currentPage > 1) {
			setAllowPrevious(true);
		} else {
			setAllowPrevious(false);
		}

		if (currentPage < totalPaginatedEntries) {
			setAllowNext(true);
		} else {
			setAllowNext(false);
		}

		setCurrentPage(page);
	};

	const setupPaginationItems = () => {
		const pagination = [];
		const index = Math.floor(currentPage / pagesPerGroup - 0.1);
		const chunkedPaginatedSections = [];

		let sectionIndex = 0;
		while (sectionIndex < chunkedPaginatedEntries.length) {
			chunkedPaginatedSections.push(chunkedPaginatedEntries.slice(sectionIndex, 5 + sectionIndex));
			sectionIndex += 5;
		}

		for (let i = 1; i <= chunkedPaginatedSections[index]?.length; i++) {
			pagination.push({
				type: "page",
				number: index * pagesPerGroup + i,
				label: index * pagesPerGroup + i,
			});
		}

		setPaginationItems(pagination);
		handlePrevNextPageLogic(currentPage);
	};

	useEffect(() => {
		prevCurrentPage.current = currentPage;

		if (paginationItems.length === 0) {
			setupPaginationItems();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [paginationItems]);

	useEffect(() => {
		if (prevCurrentPage.current !== currentPage) {
			setupPaginationItems();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentPage]);

	const handlePage = page => {
		handlePrevNextPageLogic(page);
	};

	const handleFirstPage = () => {
		setCurrentPage(1);
	};

	const handleLastPage = () => {
		setCurrentPage(totalPaginatedEntries);
	};

	const handlePrevPage = () => {
		setCurrentPage(currentPage - 1);
	};

	const handleNextPage = () => {
		setCurrentPage(currentPage + 1);
	};

	let Card = SecondaryCard;

	if (variant === "senary") {
		Card = SenaryCard;
	} else if (variant === "primary") {
		Card = PrimaryCard;
	} else if (variant === "tertiary") {
		Card = TertiaryCard;
	} else if (variant === "quaternary") {
		Card = QuaternaryCard;
	} else if (variant === "cruise") {
		Card = CruiseCard;
	} else if (variant === "brand") {
		Card = BrandCard;
	} else if (variant === "blog") {
		Card = BlogCard;
	} else if (variant === "single") {
		Card = SingleCard;
	} else if (variant === "noborder") {
		Card = NoBorderCard;
	} else if (variant === "square") {
		Card = SquareCard;
	}

	return (
		<div>
			{!!totalPaginatedEntries && (
				<div id={`groupsContainer${paginatedEntries.id}`}>
					<Row cols={largeTiles ? "2" : "3"} className={styles.entriesGroup}>
						{chunkedPaginatedEntries[currentPage - 1].map(entry => (
							<Cell>
								<Card
									key={entry.id}
									id={entry.id}
									header={entry.title}
									content={entry?.content?.childMarkdownRemark?.html}
									link={
										entry.link && {
											to: entry.link.url,
											target: entry.link.target,
											text: entry.title,
										}
									}
									image={entry.image}
									logo={{
										alt: entry.title,
										src: cloudinaryImageToSrcUrlAdapter(entry?.logo?.[0]),
									}}
									checklist={entry.checkListItems}
								/>
							</Cell>
						))}
					</Row>
				</div>
			)}
			{totalPaginatedEntries > 1 && (
				<ul className={styles.pagination}>
					<li>
						<button
							className={styles.prevBtnRotate}
							type="button"
							onClick={handleFirstPage}
							disabled={!allowPrevious}
							aria-label="Go to beginning">
							<Icon name="arrow-double" className={styles.icon} />
						</button>
					</li>
					<li>
						<button
							type="button"
							onClick={handlePrevPage}
							disabled={!allowPrevious}
							aria-label="Go to previous">
							<Icon name="arrow-left" className={styles.icon} />
						</button>
					</li>
					{paginationItems?.map(pagination => (
						<li>
							<button
								type="button"
								className={cx(`${currentPage === pagination?.number ? styles.active : ""}`)}
								onClick={() => handlePage(pagination?.number)}
								disabled={currentPage === pagination?.number}>
								{pagination?.label}
							</button>
						</li>
					))}
					<li>
						<button
							type="button"
							onClick={handleNextPage}
							disabled={!allowNext}
							aria-label="Go next">
							<Icon name="arrow-right" className={styles.icon} />
						</button>
					</li>
					<li>
						<button
							type="button"
							onClick={handleLastPage}
							disabled={!allowNext}
							aria-label="Go to last">
							<Icon name="arrow-double" className={styles.icon} />
						</button>
					</li>
				</ul>
			)}
		</div>
	);
};

PaginatedCollection.propTypes = {
	variant: string,
	paginatedEntries: arrayOf(
		shape({
			id: string,
			title: string,
			link: shape({
				url: string,
				target: string,
			}),
			image: shape({
				file: shape({
					url: string,
				}),
			}),
			sourceSet: arrayOf(object),
			cloudinaryImage: arrayOf(object),
			logo: arrayOf(object),
			checkListItems: arrayOf(object),
		})
	),
	entriesPerPage: number,
	largeTiles: bool,
	pagesPerGroup: number,
};
PaginatedCollection.defaultProps = {
	variant: null,
	paginatedEntries: [],
	entriesPerPage: 1,
	largeTiles: false,
	pagesPerGroup: 5,
};

export default PaginatedCollection;
export { PaginatedCollection };
